import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, support } from '../utils/api';
import { beginSupportTransition, clearSupportSession, readSupportSession, saveSupportSession, supportReturnPath } from './supportSession';
import toast from 'react-hot-toast';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [supportSession, setSupportSession] = useState(null);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const initialSupport = readSupportSession();
      if (!token) clearSupportSession();
      if (token) {
        try {
          const response = await auth.me();
          setUser(response.data);
          setSupportSession(response.data.supportSession || null);
          if (!response.data.supportSession && sessionStorage.getItem('ttic-support-end-warning')) {
            sessionStorage.removeItem('ttic-support-end-warning');
            toast.error('Returned to your account, but server closure could not be confirmed. The temporary access expires automatically; you can also end it in Admin Settings.', { duration: 8000 });
          }
        } catch (error) {
          if (initialSupport) {
            beginSupportTransition();
            clearSupportSession();
            window.location.replace(supportReturnPath(initialSupport));
            return;
          }
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await auth.login(email, password);
    const { token, user: userData } = response.data;
    clearSupportSession();
    setSupportSession(null);
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await auth.register(data);
    const { token, user: userData } = response.data;
    clearSupportSession();
    setSupportSession(null);
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = () => {
    clearSupportSession();
    setSupportSession(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const updateUser = (updates) => {
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    if (!supportSession) localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const startSupportAccess = async (targetUserId, password, reason) => {
    const response = await support.start(targetUserId, password, reason);
    saveSupportSession({ ...response.data.session, token: response.data.token });
    beginSupportTransition();
    // Reload clears any cached owner-page data; the native login stays intact.
    window.location.assign(response.data.session.targetRole === 'manager' ? '/admin' : '/dashboard');
  };

  const endSupportAccess = useCallback(async () => {
    const session = readSupportSession();
    if (!session) return;
    beginSupportTransition();
    try { await support.end(session.id); }
    catch { sessionStorage.setItem('ttic-support-end-warning', '1'); }
    finally {
      clearSupportSession();
      window.location.replace(localStorage.getItem('token') ? supportReturnPath(session) : '/login');
    }
  }, []);

  useEffect(() => {
    if (!supportSession) return undefined;
    const timeout = setTimeout(() => endSupportAccess(), Math.max(0, Date.parse(supportSession.expiresAt) - Date.now()));
    const ownerLoginChanged = event => {
      if (event.key === 'token') {
        beginSupportTransition();
        clearSupportSession();
        window.location.replace('/login');
      }
    };
    window.addEventListener('storage', ownerLoginChanged);
    return () => { clearTimeout(timeout); window.removeEventListener('storage', ownerLoginChanged); };
  }, [supportSession, endSupportAccess]);

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateUser,
    supportSession,
    startSupportAccess,
    endSupportAccess,
    isAuthenticated: !!user,
    isAdmin: user?.isAdmin || false,
    isSuperAdmin: user?.adminRole === 'super_admin',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
